<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Header with Background -->
    <div class="bg-blue-800 pt-24 pb-12 relative">
      <div class="absolute inset-0 bg-gradient-to-br from-blue-900/60 via-blue-800/40 to-indigo-900/50"></div>
      <div class="absolute inset-0 bg-blue-900/50"></div>
      <div class="container mx-auto px-4 relative z-10">
        <h1 class="text-3xl sm:text-4xl font-extrabold text-white mb-4 text-center tracking-tight">{{ $t('personalities.title') }}</h1>
        <p class="text-blue-200 text-center max-w-2xl mx-auto">
          {{ $t('personalities.subtitle') }}
        </p>
      </div>
    </div>

    <div class="container mx-auto px-4 -mt-10 relative z-20">
      <!-- Search and Filter Panel -->
      <div class="bg-white rounded-lg shadow-lg p-4 mb-6 sticky top-4 z-30">
        <div class="flex flex-col sm:flex-row gap-4 items-end">
          <div class="flex-1">
            <label for="search" class="block text-sm font-medium text-gray-700 mb-1">{{ $t('personalities.searchLabel') }}</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="fas fa-search text-gray-400"></i>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                id="search"
                :placeholder="$t('personalities.searchPlaceholder')"
                class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div class="w-full sm:w-auto">
            <label for="itemsPerPage" class="block text-sm font-medium text-gray-700 mb-1">{{ $t('personalities.showLabel') }}</label>
            <select v-model="itemsPerPage" id="itemsPerPage" class="w-full py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
              <option value="8">{{ $t('personalities.perPage', { count: 8 }) }}</option>
              <option value="12">{{ $t('personalities.perPage', { count: 12 }) }}</option>
              <option value="24">{{ $t('personalities.perPage', { count: 24 }) }}</option>
              <option value="48">{{ $t('personalities.perPage', { count: 48 }) }}</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading Skeleton -->
      <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
        <div v-for="n in itemsPerPage" :key="`skel-${n}`" class="bg-white rounded-lg shadow-md p-6 flex flex-col items-center animate-pulse">
            <div class="h-24 w-24 rounded-full bg-gray-300 mb-4"></div>
            <div class="h-6 w-3/4 rounded bg-gray-300 mb-2"></div>
            <div class="h-4 w-1/2 rounded bg-gray-300"></div>
        </div>
      </div>

      <!-- Personalities Grid -->
      <div v-else-if="paginatedData.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
        <div 
          v-for="person in paginatedData" 
          :key="person.id"
          @click="navigateToDetail(person)"
          class="bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center transform hover:-translate-y-1 transition-transform duration-300 cursor-pointer group"
        >
          <img 
            :src="person.photo" 
            :alt="person.name"
            @error="handleImageError"
            class="h-24 w-24 rounded-full object-cover mx-auto mb-4 border-4 border-gray-200 group-hover:border-blue-500 transition-colors"
          >
          <h3 class="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{{ person.name }}</h3>
          <p class="text-sm text-gray-500 mb-4">{{ person.title }}</p>
          
          <div class="w-full mt-auto pt-4 border-t border-gray-100">
            <div v-if="person.validSocialMedia.length > 0" class="space-y-2 text-left">
              <a 
                v-for="link in person.validSocialMedia"
                :key="link.platform"
                :href="link.url"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
                class="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
                :title="link.url"
              >
                <i :class="getSocialIcon(link.platform)" class="w-5 text-center mr-2 text-gray-400 group-hover:text-blue-500 transition-colors"></i>
                <span class="truncate">{{ platformLabel(link.platform) }}</span>
              </a>
            </div>
             <div v-else class="text-xs text-gray-400 italic">{{ $t('personalities.noSocials') }}</div>
          </div>
        </div>
      </div>

      <!-- No Results Message -->
      <div v-else class="text-center py-16 bg-white rounded-lg shadow-md mb-8">
        <i class="fas fa-search-minus fa-3x text-gray-400 mb-4"></i>
        <h3 class="text-xl font-medium text-gray-800">{{ $t('personalities.noResultsTitle') }}</h3>
        <p class="text-gray-500 mt-2">{{ $t('personalities.noResultsSubtitle', { query: searchQuery }) }}</p>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex justify-center">
         <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button @click="currentPage = Math.max(1, currentPage - 1)" :disabled="currentPage === 1" class="pagination-btn rounded-l-md" :class="{ 'opacity-50 cursor-not-allowed': currentPage === 1 }">
            <span class="sr-only">{{ $t('common.previous') }}</span><i class="fas fa-chevron-left h-5 w-5"></i>
          </button>
          <button v-for="page in visiblePages" :key="page" @click="currentPage = page" class="pagination-btn" :class="{ 'pagination-btn-active': currentPage === page }">
            {{ page }}
          </button>
          <button @click="currentPage = Math.min(totalPages, currentPage + 1)" :disabled="currentPage === totalPages" class="pagination-btn rounded-r-md" :class="{ 'opacity-50 cursor-not-allowed': currentPage === totalPages }">
            <span class="sr-only">{{ $t('common.next') }}</span><i class="fas fa-chevron-right h-5 w-5"></i>
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { usePublicPersonalityStore } from '@/stores/personalityStore';

export default {
  name: 'PublicPersonalities',
  data() {
    return {
      searchQuery: '',
      currentPage: 1,
      itemsPerPage: 12,
    };
  },
  computed: {
    ...mapStores(usePublicPersonalityStore),
    loading() {
      return this.publicPersonalityStore.isLoading;
    },
    errorMessage() {
      return this.publicPersonalityStore.error;
    },
    allPersonalities() {
      return this.publicPersonalityStore.personalitiesList;
    },
    filteredData() {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) return this.allPersonalities;
      return this.allPersonalities.filter(person => 
        (person.name && person.name.toLowerCase().includes(query)) || 
        (person.title && person.title.toLowerCase().includes(query))
      );
    },
    totalPages() {
      return Math.ceil(this.filteredData.length / this.itemsPerPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },
    visiblePages() {
        const pages = [];
        const total = this.totalPages;
        const current = this.currentPage;
        if (total <= 7) {
            for (let i = 1; i <= total; i++) pages.push(i);
        } else {
            pages.push(1);
            if (current > 3) pages.push('...');
            if (current > 2) pages.push(current - 1);
            if (current > 1 && current < total) pages.push(current);
            if (current < total - 1) pages.push(current + 1);
            if (current < total - 2) pages.push('...');
            pages.push(total);
        }
        return pages.filter((value, index, self) => self.indexOf(value) === index);
    },
  },
  methods: {
    navigateToDetail(person) {
        this.publicPersonalityStore.setCurrentPersonality(person);
        this.$router.push(`/public-personalities/${encodeURIComponent(person.name)}`);
        // this.$router.push({ 
        //     name: 'PublicPersonalityDetail',
        //     params: { lang: this.$i18n.locale, name: person.name }
        // });
    },
    handleImageError(event) {
        event.target.src = '/assets/images/default-avatar.png';
    },
    getSocialIcon(platform) {
        const icons = {
            facebook: 'fab fa-facebook-f', twitter: 'fab fa-twitter', linkedin: 'fab fa-linkedin-in',
            youtube: 'fab fa-youtube', tiktok: 'fab fa-tiktok', instagram: 'fab fa-instagram',
            email: 'fas fa-envelope',
        };
        return icons[platform] || 'fas fa-link';
    },
    platformLabel(platform) {
        return platform.charAt(0).toUpperCase() + platform.slice(1);
    },
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    itemsPerPage() { this.currentPage = 1; },
    '$i18n.locale'(newLang) {
        this.publicPersonalityStore.fetchPersonalitiesList(newLang);
    }
  },
  created() {
    this.publicPersonalityStore.fetchPersonalitiesList(this.$i18n.locale);
  }
};
</script>

<style scoped>
.pagination-btn {
  @apply relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50;
}
.pagination-btn-active {
  @apply z-10 bg-blue-50 border-blue-500 text-blue-600;
}
</style>